import React, { useState } from 'react';
import type { CSSProperties, ReactNode, FormEventHandler } from 'react';
import useStyles from '../../theme/useStyles';
import { useReadOnly } from '../../utils/readOnly';
import { requiredError } from '../../utils/requiredFields';

type RequiredControl = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

/** The label text a control was rendered with, without the asterisk. */
const labelOf = (el: RequiredControl): string => {
  const text = el.labels?.[0]?.textContent
    || el.closest('div')?.querySelector('label')?.textContent
    || el.getAttribute('aria-label')
    || el.getAttribute('placeholder')
    || el.name
    || 'A required field';
  return text.replace(/\*/g, '').trim();
};

/**
 * Every required control left empty, by label. The browser's own check stops
 * at the first one and shows a tooltip; a parish office typing a long form
 * wants the whole list in one go (CHM-054).
 */
const emptyRequired = (form: HTMLFormElement): RequiredControl[] =>
  Array.from(form.querySelectorAll<RequiredControl>('[required]')).filter((el) => {
    if (el.disabled) return false;
    if (el instanceof HTMLInputElement && (el.type === 'checkbox' || el.type === 'radio')) return !el.checked;
    return String(el.value ?? '').trim() === '';
  });

// The card-with-purple-header form shell every module builds by hand.
//
//   <FormLayout title="Schedule New Event" onSubmit={handleSubmit}
//     onCancel={() => setView('list')} submitLabel="Save & Schedule">
//     <FormLayout.Grid>
//       <Input label="Venue" ... fullWidth />
//     </FormLayout.Grid>
//   </FormLayout>
export interface FormLayoutProps {
  title?: ReactNode;
  onSubmit?: FormEventHandler<HTMLFormElement>;
  /** Omit to hide the cancel button entirely. */
  onCancel?: () => void;
  cancelLabel?: string;
  submitLabel?: string;
  submitting?: boolean;
  /**
   * Blocks submission without the "Saving…" label — for prerequisites that are
   * not yet met (e.g. no Academic Year exists yet, so a Class cannot be created).
   */
  submitDisabled?: boolean;
  maxWidth?: string;
  /** Extra buttons rendered to the left of Submit. */
  actions?: ReactNode;
  children?: ReactNode;
  isDarkMode?: boolean;
}

export interface FormGridProps {
  columns?: number;
  children?: ReactNode;
  style?: CSSProperties;
  isDarkMode?: boolean;
}

export interface FormSectionProps {
  title?: ReactNode;
  children?: ReactNode;
  isDarkMode?: boolean;
}

const FormLayout = ({
  title,
  onSubmit,
  onCancel,
  cancelLabel = 'Cancel',
  submitLabel = 'Save',
  submitting = false,
  submitDisabled = false,
  maxWidth = '800px',
  actions,
  children,
  isDarkMode
}: FormLayoutProps) => {
  const s = useStyles(isDarkMode);
  const readOnly = useReadOnly();
  const [missing, setMissing] = useState<string[]>([]);

  // Required fields are checked here, all at once, before the module's own
  // submit runs; the other browser constraints (email shape, min/max) still
  // get the native tooltip through reportValidity.
  const handleSubmit: FormEventHandler<HTMLFormElement> = (e) => {
    if (readOnly) { e.preventDefault(); return; }
    const form = e.currentTarget;
    const empty = emptyRequired(form);
    if (empty.length) {
      e.preventDefault();
      setMissing(Array.from(new Set(empty.map(labelOf))));
      empty[0].focus();
      return;
    }
    if (!form.checkValidity()) {
      e.preventDefault();
      form.reportValidity();
      return;
    }
    setMissing([]);
    onSubmit?.(e);
  };

  return (
    <div style={{ ...s.formWrapper, maxWidth }}>
      <div style={s.formHeaderContainer}>
        <h3 style={s.formHeaderTitle}>{title}</h3>
        {onCancel && <button type="button" onClick={onCancel} style={s.backBtn}>{cancelLabel}</button>}
      </div>
      {/* Hiding the button is not enough — Enter in a text field submits a form
          on its own, so the handler itself is withheld in read-only mode. */}
      <form data-edit-form noValidate onSubmit={handleSubmit} onInput={() => { if (missing.length) setMissing([]); }} style={s.formBody}>
        {children}
        {missing.length > 0 && (
          <div role="alert" style={{
            margin: '4px 0 12px', padding: '10px 14px', borderRadius: s.t.radius.lg,
            border: `1px solid ${s.t.danger}`, backgroundColor: s.t.bgSubtle,
            color: s.t.danger, fontSize: s.t.fontSize.md, fontWeight: s.t.fontWeight.bold
          }}>
            {requiredError(missing)}
          </div>
        )}
        <div style={s.formFooter}>
          {actions}
          {readOnly ? (
            <span style={{ fontSize: s.t.fontSize.base, color: s.t.textMuted }}>
              You have read-only access to this module, so this form cannot be saved.
            </span>
          ) : (
            <button
              type="submit"
              disabled={submitting || submitDisabled}
              style={{
                ...s.submitBtn,
                ...(submitting || submitDisabled ? { opacity: 0.6, cursor: 'not-allowed' } : {})
              }}
            >
              {submitting ? 'Saving…' : submitLabel}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

/**
 * `columns` is the count on a roomy screen. Below 760px the grid drops to one
 * column — see `.fl-grid` in index.css, since a media query cannot be expressed
 * as an inline style. `gridTemplateColumns` is deliberately not set here so the
 * stylesheet stays in control; pass it via `style` only to override both.
 */
const Grid = ({ columns = 2, children, style, isDarkMode }: FormGridProps) => {
  const s = useStyles(isDarkMode);
  const { gridTemplateColumns, ...grid } = s.formGrid as CSSProperties;
  return (
    <div
      className="fl-grid"
      style={{ ...grid, ['--fl-cols' as string]: columns, ...style }}
    >
      {children}
    </div>
  );
};

const Section = ({ title, children, isDarkMode }: FormSectionProps) => {
  const s = useStyles(isDarkMode);
  return (
    <>
      <div style={s.sectionTitle}>{title}</div>
      {children}
    </>
  );
};

FormLayout.Grid = Grid;
FormLayout.Section = Section;

export default FormLayout;
